/**
 * CredentialDialog Component
 * Unified modal for creating and editing credentials with tabbed interface
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { credentialsApi } from '@/lib/api';
import { getCredentialDefinition } from '@/utils/credentialDefinitions';
import { CredentialIcon } from './CredentialIcon';
import { CredentialIconGrid } from './CredentialIconGrid';
import { CredentialDetailsTab, CredentialTestingTab, CredentialHelpTab } from './tabs';
import { MicrosoftTeamsOAuthForm } from './MicrosoftTeamsOAuthForm';
import { MicrosoftOutlookOAuthForm } from './MicrosoftOutlookOAuthForm';
import { AsanaOAuth2Form } from './AsanaOAuth2Form';
import { FTPCredentialForm } from './FTPCredentialForm';
import { MsSqlCredentialForm } from './MsSqlCredentialForm';
import { PostgreSqlCredentialForm } from './PostgreSqlCredentialForm';
import { MySqlCredentialForm } from './MySqlCredentialForm';

/** Standard masked value for sensitive fields (8 bullet characters) */
export const MASKED_VALUE = '••••••••';

interface Credential {
  id: string;
  name: string;
  type: string;
  created_at?: string;
  updated_at?: string;
}

interface CredentialDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;

  /** Existing credential for editing (null for create mode) */
  credential?: Credential | null;

  /** Pre-selected credential type (for create mode) */
  initialType?: string;

  /** Filter to only show specific credential types */
  allowedTypes?: string[];

  /** Callback when credential is created/updated successfully */
  onSuccess?: (credentialId: string) => void;
}

// Initial form data structure
const getInitialFormData = () => ({
  name: '',
  type: '',
  // Generic fields
  value: '',
  username: '',
  password: '',
  headerName: '',
  endpoint: '',
  host: '',
  port: '',
  database: '',
  region: '',
  accessKeyId: '',
  secretAccessKey: '',
  projectId: '',
  url: '',
  authToken: '',
  server: '',
  encrypt: false,
  domain: '',
  connectTimeout: '',
  requestTimeout: '',
  trustServerCertificate: false,
  tdsVersion: '',
  enableArithAbort: false,
  clientId: '',
  clientSecret: '',
  tenantId: '',
  scopes: '',
  // Databricks flat fields
  workspaceUrl: '',
  databricksToken: '',
  warehouseId: '',
  // Specialized form data objects
  ftpData: {},
  msSqlData: {},
  mySqlData: {},
  mongoDBData: {},
  postgreSqlData: {},
  redisData: {},
  elasticsearchData: {},
  azureBlobStorageData: {},
  accessDocumentData: {},
  donorfyData: {},
  peopleHRData: {},
  paycircleData: {},
  accessCharityCrmData: {},
  accessAgenticPlatformData: {},
  teamsOAuthData: {},
  outlookOAuthData: {},
  entraOAuthData: {},
  sharePointOAuthData: {},
  oneDriveOAuthData: {},
  asanaOAuthData: {},
});

/**
 * Unified credential dialog with icon grid selection and tabbed form
 */
export const CredentialDialog: React.FC<CredentialDialogProps> = ({
  open,
  onOpenChange,
  credential,
  initialType,
  allowedTypes,
  onSuccess,
}) => {
  // Phase: 'selection' (icon grid) or 'form' (tabbed form)
  const [phase, setPhase] = useState<'selection' | 'form'>('selection');
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState<any>(getInitialFormData());
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);

  // Determine if we're in edit mode
  const isEditing = !!credential;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (credential) {
        // Edit mode: skip selection, load credential data
        setPhase('form');
        setSelectedType(credential.type);
        loadCredentialData(credential.id);
      } else if (initialType) {
        // Create mode with pre-selected type
        setPhase('form');
        setSelectedType(initialType);
        setFormData({ ...getInitialFormData(), type: initialType });
      } else if (allowedTypes && allowedTypes.length === 1) {
        // Only one type allowed, skip selection
        setPhase('form');
        setSelectedType(allowedTypes[0]);
        setFormData({ ...getInitialFormData(), type: allowedTypes[0] });
      } else {
        // Create mode: show selection grid
        setPhase('selection');
        setSelectedType('');
        setFormData(getInitialFormData());
      }
      setActiveTab('general');
    }
  }, [open, credential, initialType, allowedTypes]);

  // Load credential data for editing
  const loadCredentialData = async (id: string) => {
    try {
      const response = await credentialsApi.get(id);
      const data = response.data;

      // Map credential data to form data
      const newFormData = { ...getInitialFormData() };
      newFormData.name = data.name || '';
      newFormData.type = data.type || '';

      // Map type-specific data
      const credData = data.data || {};

      switch (data.type) {
        case 'api_key':
        case 'bearer_token':
        case 'claude':
        case 'openai':
        case 'data-engine':
        case 'sendgrid_api':
          newFormData.value = (credData.value || credData.apiKey) ? MASKED_VALUE : '';
          break;

        case 'azure-openai':
          newFormData.endpoint = credData.endpoint || '';
          newFormData.value = credData.apiKey ? MASKED_VALUE : '';
          break;

        case 'basic_auth':
          newFormData.username = credData.username || '';
          newFormData.password = credData.password ? MASKED_VALUE : '';
          break;

        case 'api_key_header':
          newFormData.headerName = credData.headerName || '';
          newFormData.value = credData.value ? MASKED_VALUE : '';
          break;

        case 'msSqlCredential':
          newFormData.msSqlData = {
            server: credData.server || '',
            port: credData.port || '1433',
            database: credData.database || '',
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            encrypt: credData.encrypt ?? false,
            trustServerCertificate: credData.trustServerCertificate ?? false,
            tdsVersion: credData.tdsVersion || '',
            enableArithAbort: credData.enableArithAbort ?? false,
          };
          break;

        case 'postgresql':
          newFormData.postgreSqlData = {
            host: credData.host || '',
            port: credData.port || '5432',
            database: credData.database || '',
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            sslMode: credData.sslMode || 'Prefer',
            schema: credData.schema || 'public',
          };
          break;

        case 'mysql':
          newFormData.mySqlData = {
            host: credData.host || '',
            port: credData.port || '3306',
            database: credData.database || '',
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            sslMode: credData.sslMode || 'Preferred',
          };
          break;

        case 'mongodb':
          newFormData.mongoDBData = {
            connectionString: credData.connectionString ? MASKED_VALUE : '',
            host: credData.host || '',
            port: credData.port || '27017',
            database: credData.database || '',
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            authSource: credData.authSource || 'admin',
            ssl: credData.ssl ?? false,
            useConnectionString: !!credData.connectionString,
          };
          break;

        case 'redis':
          newFormData.redisData = {
            host: credData.host || '',
            port: credData.port || '6379',
            password: credData.password ? MASKED_VALUE : '',
            database: credData.database || '0',
            ssl: credData.ssl ?? false,
          };
          break;

        case 'ftp':
          newFormData.ftpData = {
            protocol: credData.protocol || 'ftp',
            host: credData.host || '',
            port: credData.port || 21,
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            privateKey: credData.privateKey ? MASKED_VALUE : '',
            passphrase: credData.passphrase ? MASKED_VALUE : '',
            timeout: credData.timeout || 60000,
            ssl: credData.ssl || 'none',
            enableSsh: credData.enableSsh ?? false,
            sshMode: credData.sshMode || 'password',
            passive: credData.passive ?? true,
            authType: credData.authType || 'credentials',
          };
          break;

        case 'azure_blob_storage':
          newFormData.azureBlobStorageData = {
            authMethod: credData.authMethod || 'storageKey',
            accountName: credData.accountName || '',
            accountKey: credData.accountKey ? MASKED_VALUE : '',
            connectionString: credData.connectionString ? MASKED_VALUE : '',
            sasToken: credData.sasToken ? MASKED_VALUE : '',
          };
          break;

        case 'accessDocument':
          newFormData.accessDocumentData = {
            instanceId: credData.instanceId || '',
            sharedSecret: credData.sharedSecret ? MASKED_VALUE : '',
            webServiceUrl: credData.webServiceUrl || '',
          };
          break;

        case 'donorfy':
          newFormData.donorfyData = {
            tenantId: credData.tenantId || '',
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
          };
          break;

        case 'peoplehr':
          newFormData.peopleHRData = {
            apiKey: credData.apiKey ? MASKED_VALUE : '',
          };
          break;

        case 'paycircle':
          newFormData.paycircleData = {
            username: credData.username || '',
            password: credData.password ? MASKED_VALUE : '',
            baseUrl: credData.baseUrl || '',
          };
          break;

        case 'access-charity-crm':
          newFormData.accessCharityCrmData = {
            apiKey: credData.apiKey ? MASKED_VALUE : '',
            bearerToken: credData.bearerToken ? MASKED_VALUE : '',
            baseUrl: credData.baseUrl || '',
          };
          break;

        case 'access-agentic-platform':
          newFormData.accessAgenticPlatformData = {
            url: credData.url || '',
            apiKey: credData.apiKey ? MASKED_VALUE : '',
          };
          break;

        case 'turso':
          newFormData.url = credData.url || '';
          newFormData.authToken = credData.authToken ? MASKED_VALUE : '';
          break;

        case 'databricks':
          // Support both flat fields and legacy nested databricksData structure
          console.log('[CredentialDialog] Loading databricks credential, raw credData:', credData);
          const dbData = credData.databricksData || {};
          newFormData.workspaceUrl = credData.workspaceUrl || dbData.workspaceUrl || '';
          newFormData.databricksToken = (credData.token || dbData.token) ? MASKED_VALUE : '';
          newFormData.warehouseId = credData.warehouseId || dbData.warehouseId || '';
          console.log('[CredentialDialog] Loaded databricks formData:', {
            workspaceUrl: newFormData.workspaceUrl,
            databricksToken: newFormData.databricksToken ? '***' : 'empty',
            warehouseId: newFormData.warehouseId,
          });
          break;

        case 'aws':
          newFormData.accessKeyId = credData.accessKeyId || '';
          newFormData.secretAccessKey = credData.secretAccessKey ? MASKED_VALUE : '';
          newFormData.region = credData.region || '';
          break;

        case 'gcp':
          newFormData.projectId = credData.projectId || '';
          newFormData.value = credData.serviceAccountKey ? MASKED_VALUE : '';
          break;

        case 'smtp':
          newFormData.host = credData.host || '';
          newFormData.port = credData.port || '587';
          newFormData.username = credData.user || '';
          newFormData.password = credData.password ? MASKED_VALUE : '';
          break;

        case 'microsoft_teams_oauth':
          newFormData.teamsOAuthData = {
            tenantId: credData.tenantId || '',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
          };
          break;

        case 'microsoft_outlook_oauth':
          newFormData.outlookOAuthData = {
            tenantId: credData.tenantId || '',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
            scopes: credData.scopes || '',
            useSharedMailbox: credData.useSharedMailbox ?? false,
            sharedMailboxEmail: credData.sharedMailboxEmail || '',
          };
          break;

        case 'microsoft_entra_oauth':
          newFormData.entraOAuthData = {
            tenantId: credData.tenantId || '',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
            scopes: credData.scopes || '',
          };
          break;

        case 'microsoft_sharepoint_oauth':
          newFormData.sharePointOAuthData = {
            tenantId: credData.tenantId || '',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
          };
          break;

        case 'microsoft_onedrive_oauth':
          newFormData.oneDriveOAuthData = {
            tenantId: credData.tenantId || '',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
          };
          break;

        case 'asana_oauth2':
          newFormData.asanaOAuthData = {
            authMode: credData.authMode || 'oauth2',
            clientId: credData.clientId || '',
            clientSecret: credData.clientSecret ? MASKED_VALUE : '',
            personalAccessToken: credData.personalAccessToken ? MASKED_VALUE : '',
          };
          break;
      }

      setFormData(newFormData);
    } catch (error) {
      console.error('Failed to load credential:', error);
      toast.error('Failed to load credential data');
    }
  };

  // Handle type selection from grid
  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    setFormData({ ...getInitialFormData(), type });
    setActiveTab('general');
    setPhase('form');
  };

  // Handle back button (return to selection)
  const handleBack = () => {
    setPhase('selection');
    setSelectedType('');
    setFormData(getInitialFormData());
    setActiveTab('general');
  };

  // Handle form data changes
  const handleFormChange = useCallback((updates: any) => {
    setFormData((prev: any) => ({ ...prev, ...updates }));
  }, []);

  // Build test payload for testing tab
  const buildTestPayload = useCallback(() => {
    const type = selectedType;
    const data: any = {};

    // Build test payload based on type
    switch (type) {
      case 'api_key':
      case 'bearer_token':
      case 'claude':
      case 'openai':
      case 'data-engine':
      case 'sendgrid_api':
        if (!formData.value || formData.value === MASKED_VALUE) {
          toast.error('Please enter a valid API key');
          return null;
        }
        data.value = formData.value;
        break;

      case 'basic_auth':
        data.username = formData.username;
        data.password = formData.password;
        break;

      case 'msSqlCredential':
        const msSql = formData.msSqlData || {};
        data.server = msSql.server;
        data.port = msSql.port || '1433';
        data.database = msSql.database;
        data.username = msSql.username;
        data.password = msSql.password;
        data.encrypt = msSql.encrypt;
        data.trustServerCertificate = msSql.trustServerCertificate;
        break;

      case 'postgresql':
        const pg = formData.postgreSqlData || {};
        data.host = pg.host;
        data.port = pg.port;
        data.database = pg.database;
        data.username = pg.username;
        data.password = pg.password;
        data.sslMode = pg.sslMode;
        break;

      case 'mysql':
        const mysql = formData.mySqlData || {};
        data.host = mysql.host;
        data.port = mysql.port;
        data.database = mysql.database;
        data.username = mysql.username;
        data.password = mysql.password;
        break;

      case 'mongodb':
        const mongo = formData.mongoDBData || {};
        // Check useConnectionString flag (not just if connectionString has a value)
        if (mongo.useConnectionString && mongo.connectionString && mongo.connectionString !== MASKED_VALUE) {
          data.connectionString = mongo.connectionString;
        } else {
          data.host = mongo.host;
          data.port = mongo.port || '27017';
          data.database = mongo.database;
          data.username = mongo.username;
          data.password = mongo.password;
          data.authSource = mongo.authSource || 'admin';
          data.ssl = mongo.ssl || false;
          data.useConnectionString = false;
        }
        break;

      case 'ftp':
        const ftp = formData.ftpData || {};
        if (!ftp.host) {
          toast.error('Please enter FTP host');
          return null;
        }
        data.protocol = ftp.protocol || 'ftp';
        data.host = ftp.host;
        data.port = ftp.port || (ftp.protocol === 'sftp' ? 22 : 21);
        data.username = ftp.username;
        data.password = ftp.password;
        data.privateKey = ftp.privateKey;
        data.passphrase = ftp.passphrase;
        data.authType = ftp.authType || 'credentials';
        data.ssl = ftp.ssl || 'none';
        data.enableSsh = ftp.enableSsh ?? false;
        data.sshMode = ftp.sshMode || 'password';
        data.passive = ftp.passive ?? true;
        data.timeout = ftp.timeout || 60000;
        break;

      case 'smtp':
        data.host = formData.host;
        data.port = formData.port;
        data.user = formData.username;
        data.password = formData.password;
        break;

      case 'redis':
        const redis = formData.redisData || {};
        if (!redis.host) {
          toast.error('Please enter Redis host');
          return null;
        }
        data.host = redis.host;
        data.port = redis.port || '6379';
        data.password = redis.password;
        data.database = redis.database || '0';
        data.ssl = redis.ssl;
        break;

      case 'elasticsearchApi':
      case 'elasticsearch':
        const es = formData.elasticsearchData || {};
        if (!es.baseUrl) {
          toast.error('Please enter Elasticsearch base URL');
          return null;
        }
        data.baseUrl = es.baseUrl;
        data.authType = es.authType || 'basic';
        data.username = es.username;
        data.password = es.password;
        data.apiKey = es.apiKey;
        data.ignoreSslIssues = es.ignoreSslIssues;
        break;

      case 'azure_blob_storage':
        const azure = formData.azureBlobStorageData || {};
        data.authMethod = azure.authMethod || 'storageKey';
        data.accountName = azure.accountName;
        data.accountKey = azure.accountKey;
        data.connectionString = azure.connectionString;
        data.sasToken = azure.sasToken;
        break;

      case 'accessDocument':
        const accessDoc = formData.accessDocumentData || {};
        if (!accessDoc.instanceId) {
          toast.error('Please enter Instance ID');
          return null;
        }
        data.instanceId = accessDoc.instanceId;
        data.sharedSecret = accessDoc.sharedSecret;
        data.webServiceUrl = accessDoc.webServiceUrl;
        break;

      case 'donorfy':
        const donorfy = formData.donorfyData || {};
        if (!donorfy.tenantId) {
          toast.error('Please enter Donorfy Tenant ID');
          return null;
        }
        if (!donorfy.username) {
          toast.error('Please enter Donorfy Username');
          return null;
        }
        if (!donorfy.password || donorfy.password === MASKED_VALUE) {
          toast.error('Please enter Donorfy Password');
          return null;
        }
        data.tenantId = donorfy.tenantId;
        data.username = donorfy.username;
        data.password = donorfy.password;
        break;

      case 'peoplehr':
        const peoplehr = formData.peopleHRData || {};
        if (!peoplehr.apiKey || peoplehr.apiKey === MASKED_VALUE) {
          toast.error('Please enter PeopleHR API key');
          return null;
        }
        data.apiKey = peoplehr.apiKey;
        break;

      case 'paycircle':
        const paycircle = formData.paycircleData || {};
        if (!paycircle.username) {
          toast.error('Please enter Paycircle username');
          return null;
        }
        data.username = paycircle.username;
        data.password = paycircle.password;
        data.baseUrl = paycircle.baseUrl;
        break;

      case 'access-charity-crm':
        const charityCrm = formData.accessCharityCrmData || {};
        data.apiKey = charityCrm.apiKey;
        data.bearerToken = charityCrm.bearerToken;
        data.baseUrl = charityCrm.baseUrl;
        break;

      case 'access-agentic-platform':
        const agenticPlatform = formData.accessAgenticPlatformData || {};
        if (!agenticPlatform.url) {
          toast.error('Please enter Base URL');
          return null;
        }
        if (!agenticPlatform.apiKey || agenticPlatform.apiKey === MASKED_VALUE) {
          toast.error('Please enter API Key');
          return null;
        }
        data.url = agenticPlatform.url;
        data.apiKey = agenticPlatform.apiKey;
        break;

      case 'turso':
        if (!formData.url) {
          toast.error('Please enter Turso database URL');
          return null;
        }
        data.url = formData.url;
        data.authToken = formData.authToken;
        break;

      case 'databricks':
        if (!formData.workspaceUrl) {
          toast.error('Please enter Databricks workspace URL');
          return null;
        }
        if (!formData.databricksToken || formData.databricksToken === MASKED_VALUE) {
          toast.error('Please enter Databricks personal access token');
          return null;
        }
        data.workspaceUrl = formData.workspaceUrl;
        data.token = formData.databricksToken;
        if (formData.warehouseId) {
          data.warehouseId = formData.warehouseId;
        }
        break;

      case 'aws':
        if (!formData.accessKeyId) {
          toast.error('Please enter AWS Access Key ID');
          return null;
        }
        data.accessKeyId = formData.accessKeyId;
        data.secretAccessKey = formData.secretAccessKey;
        data.region = formData.region || 'us-east-1';
        break;

      case 'gcp':
        if (!formData.projectId) {
          toast.error('Please enter GCP Project ID');
          return null;
        }
        data.projectId = formData.projectId;
        data.serviceAccountKey = formData.value;
        break;

      case 'microsoft_teams_oauth':
        const teams = formData.teamsOAuthData || {};
        if (!teams.tenantId || !teams.clientId) {
          toast.error('Please enter Tenant ID and Client ID');
          return null;
        }
        data.tenantId = teams.tenantId;
        data.clientId = teams.clientId;
        data.clientSecret = teams.clientSecret;
        break;

      case 'microsoft_outlook_oauth':
        const outlook = formData.outlookOAuthData || {};
        if (!outlook.tenantId || !outlook.clientId) {
          toast.error('Please enter Tenant ID and Client ID');
          return null;
        }
        data.tenantId = outlook.tenantId;
        data.clientId = outlook.clientId;
        data.clientSecret = outlook.clientSecret;
        data.scopes = outlook.scopes;
        data.useSharedMailbox = outlook.useSharedMailbox;
        data.sharedMailboxEmail = outlook.sharedMailboxEmail;
        break;

      case 'microsoft_entra_oauth':
        const entraTest = formData.entraOAuthData || {};
        if (!entraTest.tenantId || !entraTest.clientId) {
          toast.error('Please enter Tenant ID and Client ID');
          return null;
        }
        data.tenantId = entraTest.tenantId;
        data.clientId = entraTest.clientId;
        data.clientSecret = entraTest.clientSecret;
        break;

      case 'microsoft_sharepoint_oauth':
        const spTest = formData.sharePointOAuthData || {};
        if (!spTest.tenantId || !spTest.clientId) {
          toast.error('Please enter Tenant ID and Client ID');
          return null;
        }
        data.tenantId = spTest.tenantId;
        data.clientId = spTest.clientId;
        data.clientSecret = spTest.clientSecret;
        break;

      case 'microsoft_onedrive_oauth':
        const odTest = formData.oneDriveOAuthData || {};
        if (!odTest.tenantId || !odTest.clientId) {
          toast.error('Please enter Tenant ID and Client ID');
          return null;
        }
        data.tenantId = odTest.tenantId;
        data.clientId = odTest.clientId;
        data.clientSecret = odTest.clientSecret;
        break;

      case 'asana_oauth2':
        const asanaTest = formData.asanaOAuthData || {};
        data.authMode = asanaTest.authMode || 'oauth2';
        if (asanaTest.authMode === 'pat') {
          if (!asanaTest.personalAccessToken) {
            toast.error('Please enter a Personal Access Token');
            return null;
          }
          data.personalAccessToken = asanaTest.personalAccessToken;
        } else {
          if (!asanaTest.clientId) {
            toast.error('Please enter Client ID');
            return null;
          }
          data.clientId = asanaTest.clientId;
          data.clientSecret = asanaTest.clientSecret;
        }
        break;

      case 'azure-openai':
        if (!formData.endpoint) {
          toast.error('Please enter Azure OpenAI endpoint');
          return null;
        }
        data.endpoint = formData.endpoint;
        data.apiKey = formData.value;
        break;

      case 'api_key_header':
        if (!formData.headerName) {
          toast.error('Please enter header name');
          return null;
        }
        data.headerName = formData.headerName;
        data.value = formData.value;
        break;

      default:
        data.value = formData.value;
    }

    return { type, data };
  }, [selectedType, formData]);

  // Handle save
  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Credential name is required');
      setActiveTab('general');
      return;
    }

    try {
      setSaving(true);

      const payload: any = {
        name: formData.name,
        type: selectedType,
      };

      // Build payload based on credential type
      switch (selectedType) {
        case 'api_key':
        case 'claude':
        case 'openai':
        case 'data-engine':
        case 'sendgrid_api':
          if (!formData.value || formData.value === MASKED_VALUE) {
            if (!isEditing) {
              toast.error('API Key is required');
              return;
            }
          } else {
            payload.value = formData.value;
          }
          break;

        case 'azure-openai':
          payload.endpoint = formData.endpoint;
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.apiKey = formData.value;
          }
          break;

        case 'bearer_token':
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
          break;

        case 'basic_auth':
          payload.username = formData.username;
          if (formData.password && formData.password !== MASKED_VALUE) {
            payload.password = formData.password;
          }
          break;

        case 'api_key_header':
          payload.headerName = formData.headerName;
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
          break;

        case 'msSqlCredential':
          const msSql = formData.msSqlData || {};
          payload.server = msSql.server;
          payload.port = msSql.port || '1433';
          payload.database = msSql.database;
          payload.username = msSql.username;
          if (msSql.password && msSql.password !== MASKED_VALUE) {
            payload.password = msSql.password;
          }
          payload.encrypt = msSql.encrypt;
          payload.trustServerCertificate = msSql.trustServerCertificate;
          if (msSql.tdsVersion) payload.tdsVersion = msSql.tdsVersion;
          payload.enableArithAbort = msSql.enableArithAbort;
          break;

        case 'postgresql':
          const pg = formData.postgreSqlData || {};
          payload.host = pg.host;
          payload.port = pg.port || '5432';
          payload.database = pg.database;
          payload.username = pg.username;
          if (pg.password && pg.password !== MASKED_VALUE) {
            payload.password = pg.password;
          }
          payload.sslMode = pg.sslMode || 'Prefer';
          payload.schema = pg.schema || 'public';
          break;

        case 'mysql':
          const mysql = formData.mySqlData || {};
          payload.host = mysql.host;
          payload.port = mysql.port || '3306';
          payload.database = mysql.database;
          payload.username = mysql.username;
          if (mysql.password && mysql.password !== MASKED_VALUE) {
            payload.password = mysql.password;
          }
          payload.sslMode = mysql.sslMode || 'Preferred';
          break;

        case 'mongodb':
          const mongo = formData.mongoDBData || {};
          // Check useConnectionString flag (not just if connectionString has a value)
          if (mongo.useConnectionString && mongo.connectionString && mongo.connectionString !== MASKED_VALUE) {
            payload.connectionString = mongo.connectionString;
          } else {
            // Send individual fields
            payload.host = mongo.host;
            payload.port = mongo.port || '27017';
            payload.database = mongo.database;
            payload.username = mongo.username;
            if (mongo.password && mongo.password !== MASKED_VALUE) {
              payload.password = mongo.password;
            }
            payload.authSource = mongo.authSource || 'admin';
            payload.ssl = mongo.ssl;
            // Explicitly set useConnectionString to false so backend knows
            payload.useConnectionString = false;
          }
          break;

        case 'redis':
          const redis = formData.redisData || {};
          payload.host = redis.host;
          payload.port = redis.port || '6379';
          if (redis.password && redis.password !== MASKED_VALUE) {
            payload.password = redis.password;
          }
          payload.database = redis.database || '0';
          payload.ssl = redis.ssl || false;
          break;

        case 'ftp':
          const ftp = formData.ftpData || {};
          payload.protocol = ftp.protocol || 'ftp';
          payload.host = ftp.host;
          payload.port = String(ftp.port || (ftp.protocol === 'sftp' ? 22 : 21));
          payload.username = ftp.username;
          if (ftp.password && ftp.password !== MASKED_VALUE) {
            payload.password = ftp.password;
          }
          payload.privateKey = ftp.privateKey;
          payload.passphrase = ftp.passphrase;
          payload.timeout = ftp.timeout || 60000;
          payload.ssl = ftp.ssl || 'none';
          payload.enableSsh = ftp.enableSsh ?? (ftp.protocol === 'sftp');
          payload.sshMode = ftp.sshMode || 'password';
          payload.passive = ftp.passive ?? true;
          payload.authType = ftp.authType || 'credentials';
          break;

        case 'azure_blob_storage':
          const azure = formData.azureBlobStorageData || {};
          payload.authMethod = azure.authMethod || 'storageKey';
          payload.accountName = azure.accountName || '';
          if (azure.accountKey && azure.accountKey !== MASKED_VALUE) {
            payload.accountKey = azure.accountKey;
          }
          if (azure.connectionString && azure.connectionString !== MASKED_VALUE) {
            payload.connectionString = azure.connectionString;
          }
          if (azure.sasToken && azure.sasToken !== MASKED_VALUE) {
            payload.sasToken = azure.sasToken;
          }
          break;

        case 'elasticsearchApi':
        case 'elasticsearch':
          const esData = formData.elasticsearchData || {};
          payload.baseUrl = esData.baseUrl;
          payload.authType = esData.authType || 'basic';
          payload.username = esData.username;
          if (esData.password && esData.password !== MASKED_VALUE) {
            payload.password = esData.password;
          }
          if (esData.apiKey && esData.apiKey !== MASKED_VALUE) {
            payload.apiKey = esData.apiKey;
          }
          payload.ignoreSslIssues = esData.ignoreSslIssues || false;
          break;

        case 'accessDocument':
          const accessDoc = formData.accessDocumentData || {};
          payload.instanceId = accessDoc.instanceId;
          if (accessDoc.sharedSecret && accessDoc.sharedSecret !== MASKED_VALUE) {
            payload.sharedSecret = accessDoc.sharedSecret;
          }
          payload.webServiceUrl = accessDoc.webServiceUrl || '';
          break;

        case 'donorfy':
          const donorfyUpdate = formData.donorfyData || {};
          payload.tenantId = donorfyUpdate.tenantId;
          payload.username = donorfyUpdate.username;
          if (donorfyUpdate.password && donorfyUpdate.password !== MASKED_VALUE) {
            payload.password = donorfyUpdate.password;
          }
          break;

        case 'peoplehr':
          const peoplehr = formData.peopleHRData || {};
          if (peoplehr.apiKey && peoplehr.apiKey !== MASKED_VALUE) {
            payload.apiKey = peoplehr.apiKey;
          }
          break;

        case 'paycircle':
          const paycircle = formData.paycircleData || {};
          payload.username = paycircle.username;
          if (paycircle.password && paycircle.password !== MASKED_VALUE) {
            payload.password = paycircle.password;
          }
          payload.baseUrl = paycircle.baseUrl || '';
          break;

        case 'access-charity-crm':
          const charity = formData.accessCharityCrmData || {};
          if (charity.apiKey && charity.apiKey !== MASKED_VALUE) {
            payload.apiKey = charity.apiKey;
          }
          if (charity.bearerToken && charity.bearerToken !== MASKED_VALUE) {
            payload.bearerToken = charity.bearerToken;
          }
          payload.baseUrl = charity.baseUrl || '';
          break;

        case 'access-agentic-platform':
          const agentic = formData.accessAgenticPlatformData || {};
          if (!agentic.url) {
            toast.error('Base URL is required');
            return;
          }
          if (!isEditing && (!agentic.apiKey || agentic.apiKey === MASKED_VALUE)) {
            toast.error('API Key is required');
            return;
          }
          payload.url = agentic.url;
          if (agentic.apiKey && agentic.apiKey !== MASKED_VALUE) {
            payload.apiKey = agentic.apiKey;
          }
          break;

        case 'turso':
          payload.url = formData.url;
          if (formData.authToken && formData.authToken !== MASKED_VALUE) {
            payload.authToken = formData.authToken;
          }
          break;

        case 'databricks':
          console.log('[CredentialDialog] Saving databricks credential, formData:', {
            workspaceUrl: formData.workspaceUrl,
            databricksToken: formData.databricksToken ? '***' : 'empty',
            warehouseId: formData.warehouseId,
          });
          if (!formData.workspaceUrl) {
            toast.error('Workspace URL is required');
            return;
          }
          if (!isEditing && (!formData.databricksToken || formData.databricksToken === MASKED_VALUE)) {
            toast.error('Personal Access Token is required');
            return;
          }
          payload.workspaceUrl = formData.workspaceUrl;
          if (formData.databricksToken && formData.databricksToken !== MASKED_VALUE) {
            payload.token = formData.databricksToken;
          }
          if (formData.warehouseId) {
            payload.warehouseId = formData.warehouseId;
          }
          console.log('[CredentialDialog] Databricks payload:', {
            ...payload,
            token: payload.token ? '***' : 'not set',
          });
          break;

        case 'aws':
          payload.accessKeyId = formData.accessKeyId;
          if (formData.secretAccessKey && formData.secretAccessKey !== MASKED_VALUE) {
            payload.secretAccessKey = formData.secretAccessKey;
          }
          payload.region = formData.region || 'us-east-1';
          break;

        case 'gcp':
          payload.projectId = formData.projectId;
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.serviceAccountKey = formData.value;
          }
          break;

        case 'smtp':
          payload.host = formData.host;
          payload.port = formData.port || '587';
          payload.user = formData.username;
          if (formData.password && formData.password !== MASKED_VALUE) {
            payload.password = formData.password;
          }
          break;

        case 'microsoft_teams_oauth':
          const teams = formData.teamsOAuthData || {};
          payload.tenantId = teams.tenantId;
          payload.clientId = teams.clientId;
          if (teams.clientSecret && teams.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = teams.clientSecret;
          }
          break;

        case 'microsoft_outlook_oauth':
          const outlook = formData.outlookOAuthData || {};
          payload.tenantId = outlook.tenantId;
          payload.clientId = outlook.clientId;
          if (outlook.clientSecret && outlook.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = outlook.clientSecret;
          }
          payload.scopes = outlook.scopes || 'offline_access,User.Read,Mail.ReadWrite,Mail.Send';
          payload.useSharedMailbox = outlook.useSharedMailbox;
          payload.sharedMailboxEmail = outlook.sharedMailboxEmail;
          break;

        case 'microsoft_entra_oauth':
          const entra = formData.entraOAuthData || {};
          payload.tenantId = entra.tenantId;
          payload.clientId = entra.clientId;
          if (entra.clientSecret && entra.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = entra.clientSecret;
          }
          payload.scopes = entra.scopes || 'offline_access,User.Read.All,Group.Read.All,GroupMember.Read.All';
          break;

        case 'microsoft_sharepoint_oauth':
          const sp = formData.sharePointOAuthData || {};
          payload.tenantId = sp.tenantId;
          payload.clientId = sp.clientId;
          if (sp.clientSecret && sp.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = sp.clientSecret;
          }
          break;

        case 'microsoft_onedrive_oauth':
          const od = formData.oneDriveOAuthData || {};
          payload.tenantId = od.tenantId;
          payload.clientId = od.clientId;
          if (od.clientSecret && od.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = od.clientSecret;
          }
          break;

        case 'asana_oauth2':
          const asana = formData.asanaOAuthData || {};
          payload.authMode = asana.authMode || 'oauth2';
          if (asana.authMode === 'pat') {
            if (asana.personalAccessToken && asana.personalAccessToken !== MASKED_VALUE) {
              payload.personalAccessToken = asana.personalAccessToken;
            }
          } else {
            payload.clientId = asana.clientId;
            if (asana.clientSecret && asana.clientSecret !== MASKED_VALUE) {
              payload.clientSecret = asana.clientSecret;
            }
          }
          break;

        default:
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
      }

      if (isEditing && credential) {
        await credentialsApi.update(credential.id, payload);
        toast.success('Credential updated successfully');
        onSuccess?.(credential.id);
      } else {
        const response = await credentialsApi.create(payload);
        const newId = response.data?.id || response.data?.credential?.id;
        toast.success('Credential created successfully');
        onSuccess?.(newId || '');
      }

      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to save credential:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to save credential';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const definition = selectedType ? getCredentialDefinition(selectedType) : null;

  // Determine if this is a credential type that needs flattened tabs
  const isMicrosoftOAuthCredential = selectedType === 'microsoft_teams_oauth' || selectedType === 'microsoft_outlook_oauth' || selectedType === 'microsoft_entra_oauth' || selectedType === 'microsoft_sharepoint_oauth' || selectedType === 'microsoft_onedrive_oauth';
  const isAsanaOAuthCredential = selectedType === 'asana_oauth2';
  const isOAuthCredential = isMicrosoftOAuthCredential || isAsanaOAuthCredential;
  const isFTPCredential = selectedType === 'ftp';
  const isDatabaseCredential = ['msSqlCredential', 'postgresql', 'mysql'].includes(selectedType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[600px] flex flex-col">
        {phase === 'selection' ? (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Add Credential</DialogTitle>
              <DialogDescription>
                Select a credential type to add
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <CredentialIconGrid
                onSelect={handleTypeSelect}
                allowedTypes={allowedTypes}
              />
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center gap-2">
                {!isEditing && !initialType && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <div className="flex items-center gap-3">
                  {definition && (
                    <CredentialIcon type={selectedType} size="md" showBackground />
                  )}
                  <div>
                    <DialogTitle>
                      {isEditing ? 'Edit' : 'New'} {definition?.label || 'Credential'}
                    </DialogTitle>
                    <DialogDescription>
                      {isEditing
                        ? 'Update your credential details'
                        : definition?.description || 'Add a new credential'}
                    </DialogDescription>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <TabsList className="flex-shrink-0">
                <TabsTrigger value="general">General</TabsTrigger>
                {isOAuthCredential ? (
                  <>
                    <TabsTrigger value="azure-ad">{isAsanaOAuthCredential ? 'App Config' : 'Azure AD'}</TabsTrigger>
                    <TabsTrigger value="connection">Connection</TabsTrigger>
                  </>
                ) : isFTPCredential ? (
                  <>
                    <TabsTrigger value="server">Server</TabsTrigger>
                    <TabsTrigger value="auth">Auth</TabsTrigger>
                    <TabsTrigger value="security">Security</TabsTrigger>
                    <TabsTrigger value="options">Options</TabsTrigger>
                  </>
                ) : isDatabaseCredential ? (
                  <>
                    <TabsTrigger value="connection">Connection</TabsTrigger>
                    <TabsTrigger value="advanced">Advanced</TabsTrigger>
                  </>
                ) : (
                  <TabsTrigger value="details">Details</TabsTrigger>
                )}
                <TabsTrigger value="testing">Testing</TabsTrigger>
                <TabsTrigger value="help">Help</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto py-4 px-1">
                <TabsContent value="general" className="mt-0">
                  <div className="space-y-4">
                    {/* Credential Type Info */}
                    {definition && (
                      <div className="flex items-center gap-3 pb-4 border-b">
                        <CredentialIcon type={selectedType} size="lg" showBackground />
                        <div>
                          <h3 className="font-medium">{definition.label}</h3>
                          <p className="text-sm text-muted-foreground">{definition.description}</p>
                        </div>
                      </div>
                    )}

                    {/* Credential Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">Credential Name *</Label>
                      <Input
                        id="name"
                        placeholder="My Credential Name"
                        value={formData.name || ''}
                        onChange={(e) => handleFormChange({ name: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        A friendly name to identify this credential
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {isOAuthCredential ? (
                  <>
                    <TabsContent value="azure-ad" className="mt-0">
                      {selectedType === 'microsoft_teams_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.teamsOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ teamsOAuthData: { ...formData.teamsOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                      {selectedType === 'microsoft_outlook_oauth' && (
                        <MicrosoftOutlookOAuthForm
                          formData={formData.outlookOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ outlookOAuthData: { ...formData.outlookOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                      {selectedType === 'microsoft_entra_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.entraOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ entraOAuthData: { ...formData.entraOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                      {selectedType === 'microsoft_sharepoint_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.sharePointOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ sharePointOAuthData: { ...formData.sharePointOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                      {selectedType === 'microsoft_onedrive_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.oneDriveOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ oneDriveOAuthData: { ...formData.oneDriveOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                      {selectedType === 'asana_oauth2' && (
                        <AsanaOAuth2Form
                          formData={formData.asanaOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ asanaOAuthData: { ...formData.asanaOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="config"
                        />
                      )}
                    </TabsContent>

                    <TabsContent value="connection" className="mt-0">
                      {selectedType === 'microsoft_teams_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.teamsOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ teamsOAuthData: { ...formData.teamsOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                      {selectedType === 'microsoft_outlook_oauth' && (
                        <MicrosoftOutlookOAuthForm
                          formData={formData.outlookOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ outlookOAuthData: { ...formData.outlookOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                      {selectedType === 'microsoft_entra_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.entraOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ entraOAuthData: { ...formData.entraOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                      {selectedType === 'microsoft_sharepoint_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.sharePointOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ sharePointOAuthData: { ...formData.sharePointOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                      {selectedType === 'microsoft_onedrive_oauth' && (
                        <MicrosoftTeamsOAuthForm
                          formData={formData.oneDriveOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ oneDriveOAuthData: { ...formData.oneDriveOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                      {selectedType === 'asana_oauth2' && (
                        <AsanaOAuth2Form
                          formData={formData.asanaOAuthData || {}}
                          onChange={(updates) =>
                            handleFormChange({ asanaOAuthData: { ...formData.asanaOAuthData, ...updates } })
                          }
                          isEditing={isEditing}
                          credentialId={credential?.id}
                          section="status"
                        />
                      )}
                    </TabsContent>
                  </>
                ) : isFTPCredential ? (
                  <>
                    <TabsContent value="server" className="mt-0">
                      <FTPCredentialForm
                        value={formData.ftpData || {}}
                        onChange={(updates) =>
                          handleFormChange({ ftpData: { ...formData.ftpData, ...updates } })
                        }
                        section="server"
                      />
                    </TabsContent>

                    <TabsContent value="auth" className="mt-0">
                      <FTPCredentialForm
                        value={formData.ftpData || {}}
                        onChange={(updates) =>
                          handleFormChange({ ftpData: { ...formData.ftpData, ...updates } })
                        }
                        section="auth"
                      />
                    </TabsContent>

                    <TabsContent value="security" className="mt-0">
                      <FTPCredentialForm
                        value={formData.ftpData || {}}
                        onChange={(updates) =>
                          handleFormChange({ ftpData: { ...formData.ftpData, ...updates } })
                        }
                        section="security"
                      />
                    </TabsContent>

                    <TabsContent value="options" className="mt-0">
                      <FTPCredentialForm
                        value={formData.ftpData || {}}
                        onChange={(updates) =>
                          handleFormChange({ ftpData: { ...formData.ftpData, ...updates } })
                        }
                        section="options"
                      />
                    </TabsContent>
                  </>
                ) : isDatabaseCredential ? (
                  <>
                    <TabsContent value="connection" className="mt-0">
                      {selectedType === 'msSqlCredential' && (
                        <MsSqlCredentialForm
                          formData={formData.msSqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ msSqlData: { ...formData.msSqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="connection"
                        />
                      )}
                      {selectedType === 'postgresql' && (
                        <PostgreSqlCredentialForm
                          formData={formData.postgreSqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ postgreSqlData: { ...formData.postgreSqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="connection"
                        />
                      )}
                      {selectedType === 'mysql' && (
                        <MySqlCredentialForm
                          formData={formData.mySqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ mySqlData: { ...formData.mySqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="connection"
                        />
                      )}
                    </TabsContent>

                    <TabsContent value="advanced" className="mt-0">
                      {selectedType === 'msSqlCredential' && (
                        <MsSqlCredentialForm
                          formData={formData.msSqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ msSqlData: { ...formData.msSqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="advanced"
                        />
                      )}
                      {selectedType === 'postgresql' && (
                        <PostgreSqlCredentialForm
                          formData={formData.postgreSqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ postgreSqlData: { ...formData.postgreSqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="advanced"
                        />
                      )}
                      {selectedType === 'mysql' && (
                        <MySqlCredentialForm
                          formData={formData.mySqlData || {}}
                          onChange={(updates) =>
                            handleFormChange({ mySqlData: { ...formData.mySqlData, ...updates } })
                          }
                          isEditing={isEditing}
                          section="advanced"
                        />
                      )}
                    </TabsContent>
                  </>
                ) : (
                  <TabsContent value="details" className="mt-0">
                    <CredentialDetailsTab
                      credentialType={selectedType}
                      isEditing={isEditing}
                      formData={formData}
                      onChange={handleFormChange}
                      credentialId={credential?.id}
                    />
                  </TabsContent>
                )}

                <TabsContent value="testing" className="mt-0">
                  <CredentialTestingTab
                    credentialType={selectedType}
                    formData={formData}
                    credentialId={credential?.id}
                    buildTestPayload={buildTestPayload}
                  />
                </TabsContent>

                <TabsContent value="help" className="mt-0">
                  <CredentialHelpTab credentialType={selectedType} />
                </TabsContent>
              </div>
            </Tabs>

            <DialogFooter className="flex-shrink-0">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.name?.trim()}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : isEditing ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CredentialDialog;
